import { firestore, functions, storage } from "../services/firebase/firebaseInit";
import {
  collection,
  doc,
  endBefore,
  getDocs,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  where,
} from "@firebase/firestore";
import { httpsCallable } from "@firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

export type WhatsappContact = {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  sector?: string;
};

export type WhatsappConversation = {
  id: string;
  contactId: string | null;
  contactName: string;
  contactPhone: string;
  queueId: string;
  status: "open" | "closed";
  assignedAgentId: string | null;
  unreadCount: number;
  createdAt: number;
  lastMessageAt: number;
  lastMessageText?: string;
  // Ausente = conversa antiga (de antes desse campo existir), ou o
  // backend não mandou. Diz quando lastMessageText é na verdade um nome de
  // arquivo (mídia sem legenda), pra não mostrar isso cru num preview.
  lastMessageType?: WhatsappMessage["type"];
  botState?: { active: boolean; path: number[]; attempts: number } | null;
};

export type WhatsappMessage = {
  id: string;
  direction: "in" | "out";
  type: "text" | "image" | "document" | "audio" | "video" | "unsupported" | "system";
  text: string;
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  zapiMessageId?: string;
  status?: "sent" | "delivered" | "read" | "failed";
  authorAgentId?: string;
  timestamp: number;
  replyToMessageId?: string;
  reactions?: { in?: string; out?: string };
  // Preenchido no cliente (não existe no Firestore) só pra saber de qual
  // subcoleção de conversa essa mensagem veio, já que a thread mescla a
  // conversa aberta atual com conversas anteriores encerradas do mesmo
  // contato — responder/reagir só funciona dentro da conversa atual (v0).
  conversationId?: string;
  // Só em mensagem de GRUPO (direction "in") — em 1:1 o remetente já é
  // óbvio (é o contato), então fica ausente.
  senderPhone?: string;
  senderName?: string;
  // Só em mensagem de GRUPO (direction "out") que menciona participante(s)
  // — telefones que aparecem como "@{telefone}" dentro de `text`.
  mentions?: string[];
  // "Apagar para todos" (mesmo modelo do WhatsApp) — quando true, text já
  // vem vazio e mediaUrl/fileName/mimeType já não existem mais. Renderizar
  // como placeholder ("Mensagem apagada") em vez do texto/mídia normal.
  deleted?: boolean;
};

export type WhatsappInstance = {
  instanceId: string;
  phoneNumber: string;
  status: "pending_qr" | "connected" | "disconnected";
};

export type WhatsappGroup = {
  id: string; // == id da Z-API "{numero}-group"
  name: string;
  description?: string;
  ownerPhone?: string;
  participants: { phone: string; name?: string; isAdmin: boolean }[];
  createdAt: number;
  lastMessageAt: number;
  lastMessageText?: string;
  lastMessageType?: WhatsappMessage["type"];
  // Ausente quando a última mensagem foi nossa (agente) — só participante
  // recebido preenche isso.
  lastMessageSenderName?: string;
  leftAt: number | null;
  unreadByUser?: Record<string, number>;
  // Ids de USUÁRIO DO VOKI (não telefone) com acesso pra ver/operar o
  // grupo dentro do Voki — vazio/ausente = aberto pra empresa inteira. Não
  // tem relação com os participantes reais do WhatsApp (`participants`
  // acima), que é sempre só o número da instância.
  accessUserIds?: string[];
};

type CallResult<T> = { error: boolean; data: T };

// Reexportado por conveniência (import site já usa "../model/whatsapp.model")
// — implementação de verdade em whatsapp.visibility.ts, que é
// propositalmente livre de qualquer import de Firebase (ver comentário lá)
// pra poder ser testado sem emulador/mocks/`.env`.
export { isConversationVisibleToAgent, computeMyQueueIds } from "./whatsapp.visibility";

// Leitura direta do Firestore (mesmo padrão já usado por filas/ramais/IVR
// em model/queue.model.ts) — escrita sempre pelas Cloud Functions abaixo.

export function watchConversations(
  companyId: string,
  onData: (list: WhatsappConversation[]) => void,
) {
  const q = query(
    collection(firestore, `companies/${companyId}/whatsappConversations`),
    where("status", "==", "open"),
  );
  return onSnapshot(q, (snap) => {
    onData(
      snap.docs.map((d) => ({ id: d.id, ...d.data() } as WhatsappConversation)),
    );
  });
}

// Mesma lógica do backend (utils/whatsappPhone.ts) duplicada aqui de
// propósito — é pouco código, sem trazer pacote de funções só por causa
// dela. Aceita telefone com ou sem DDI (o formulário de novo contato usa a
// mesma máscara "sem 55" da agenda normal do Voki) e devolve todas as
// formas que podem estar salvas: com/sem DDI e, pra número BR migrado pro
// nono dígito, com/sem o 9 — a Z-API às vezes manda um contato já
// cadastrado com o 9 sem ele no webhook (confirmado com um contato real).
export function phoneLookupVariants(phone: string): string[] {
  // Defensivo contra dado antigo/malformado com phone gravado como number.
  const digits = String(phone ?? "").replace(/\D/g, "");
  const withDdi =
    digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
  const variants = new Set([withDdi]);
  if (withDdi.length === 13 && withDdi[4] === "9") {
    variants.add(withDdi.slice(0, 4) + withDdi.slice(5));
  }
  if (withDdi.length === 12) {
    variants.add(withDdi.slice(0, 4) + "9" + withDdi.slice(4));
  }
  return Array.from(variants);
}

// Encerrar é terminal (sem reabrir — ver plano): um novo atendimento com o
// mesmo contato vira uma conversa nova, com seu próprio doc/subcoleção de
// mensagens. Sem isso, a thread mostraria só a conversa atual e o histórico
// de conversas anteriores encerradas com o mesmo telefone sumiria da tela.
// Encerradas são imutáveis (nunca recebem mensagem nova), então paginação
// única nelas; só a conversa atual precisa de listener em tempo real.
const MESSAGE_PAGE_SIZE = 30;

async function fetchMessagePage(
  companyId: string,
  conversationId: string,
  before: number | null,
): Promise<WhatsappMessage[]> {
  const constraints = before
    ? [orderBy("timestamp", "asc"), endBefore(before), limitToLast(MESSAGE_PAGE_SIZE)]
    : [orderBy("timestamp", "asc"), limitToLast(MESSAGE_PAGE_SIZE)];
  const snap = await getDocs(
    query(
      collection(
        firestore,
        `companies/${companyId}/whatsappConversations/${conversationId}/messages`,
      ),
      ...constraints,
    ),
  );
  return snap.docs.map(
    (d) => ({ id: d.id, ...d.data(), conversationId } as WhatsappMessage),
  );
}

// Carrega a thread aos poucos em vez de tudo de uma vez — várias conversas
// encerradas antigas com o mesmo contato não podem virar uma leitura
// gigante do Firestore só pra abrir a tela. A conversa ATUAL (aberta) fica
// inteira num listener ao vivo, sem paginação — ela é uma coleção só, e
// sem isso uma mensagem que muda de status (enviada→entregue→lida) depois
// de já carregada nunca atualiza na tela (bug real, achado pelo usuário:
// uma versão anterior desta função ancorava o listener no timestamp da
// última mensagem carregada, então só pegava mensagem NOVA, nunca UPDATE
// de uma já carregada). `loadMore()` só pagina pra trás nas conversas
// ENCERRADAS do mesmo contato (mais nova primeiro) — essas sim são
// imutáveis de verdade (nunca mais recebem mensagem nem mudam status).
export function createConversationThread(
  companyId: string,
  contactPhone: string,
  currentConversationId: string,
  onData: (list: WhatsappMessage[], hasMore: boolean) => void,
) {
  let disposed = false;

  let pastConvs: { id: string; createdAt: number }[] = [];
  let pastConvsLoaded = false;
  let payingConversationId: string | undefined; // conversa encerrada sendo paginada agora
  let oldestLoadedTimestamp: number | null = null;
  let exhausted = false;

  let currentMessages: WhatsappMessage[] = []; // sempre o snapshot inteiro e atual, ascendente
  const olderMessages: WhatsappMessage[] = []; // já carregadas de conversas encerradas, ascendente
  const olderIds = new Set<string>();

  function emit() {
    if (!disposed) onData([...olderMessages, ...currentMessages], !exhausted);
  }

  function prependOlder(messages: WhatsappMessage[]) {
    // `messages` já chega em ordem ascendente (ver fetchMessagePage) —
    // unshift item a item INVERTE essa ordem (cada unshift joga o anterior
    // pra depois). Precisa inserir o bloco inteiro de uma vez, preservando
    // a ordem interna dele.
    const newOnes = messages.filter((m) => !olderIds.has(m.id));
    newOnes.forEach((m) => olderIds.add(m.id));
    olderMessages.unshift(...newOnes);
  }

  const unsubscribeCurrent = onSnapshot(
    query(
      collection(
        firestore,
        `companies/${companyId}/whatsappConversations/${currentConversationId}/messages`,
      ),
      orderBy("timestamp", "asc"),
    ),
    (snap) => {
      currentMessages = snap.docs.map(
        (d) =>
          ({ id: d.id, ...d.data(), conversationId: currentConversationId } as WhatsappMessage),
      );
      emit();
    },
  );

  async function ensurePastConvsLoaded() {
    if (pastConvsLoaded) return;
    const snap = await getDocs(
      query(
        collection(firestore, `companies/${companyId}/whatsappConversations`),
        where("contactPhone", "in", phoneLookupVariants(contactPhone)),
        where("status", "==", "closed"),
      ),
    );
    pastConvs = snap.docs
      .map((d) => ({ id: d.id, createdAt: d.data().createdAt as number }))
      .filter((c) => c.id !== currentConversationId)
      .sort((a, b) => b.createdAt - a.createdAt); // mais nova primeiro
    pastConvsLoaded = true;
  }

  // Busca a próxima página mais antiga dentro das conversas ENCERRADAS
  // (mais nova primeiro) — pula pra próxima automaticamente quando uma
  // acaba, sem devolver uma página vazia à toa.
  async function loadMore() {
    if (disposed || exhausted) return;

    while (!exhausted) {
      if (payingConversationId) {
        const page = await fetchMessagePage(
          companyId,
          payingConversationId,
          oldestLoadedTimestamp,
        );
        if (page.length > 0) {
          prependOlder(page);
          oldestLoadedTimestamp = page[0].timestamp;
        }
        if (page.length < MESSAGE_PAGE_SIZE) {
          payingConversationId = undefined;
          oldestLoadedTimestamp = null;
        }
        if (page.length > 0) break;
      } else {
        await ensurePastConvsLoaded();
        const next = pastConvs.shift();
        if (!next) {
          exhausted = true;
          break;
        }
        payingConversationId = next.id;
      }
    }

    emit();
  }

  return {
    loadMore,
    dispose: () => {
      disposed = true;
      unsubscribeCurrent();
    },
  };
}

// companies/{companyId}/sharedContacts é a agenda única compartilhada —
// mesma coleção usada pela Agenda do Ramal (services/sharedContacts.js).
// Todo contato criado pelo WhatsApp já nasce nela, sem checkbox: aqui tudo
// é compartilhado por definição.
export function watchContacts(
  companyId: string,
  onData: (list: WhatsappContact[]) => void,
) {
  const q = collection(firestore, `companies/${companyId}/sharedContacts`);
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() } as WhatsappContact)));
  });
}

export function watchInstance(
  companyId: string,
  onData: (instance: WhatsappInstance | null) => void,
) {
  const ref = doc(firestore, `companies/${companyId}/whatsapp/instance`);
  return onSnapshot(ref, (snap) => {
    onData(snap.exists() ? (snap.data() as WhatsappInstance) : null);
  });
}

export async function deleteContact(contactId: string) {
  const fn = httpsCallable(functions, "deleteSharedContact");
  const result = await fn({ id: contactId });
  return result.data as CallResult<{}>;
}

export async function startConversation(payload: {
  queueId: string;
  contactId?: string;
  newContact?: {
    name: string;
    phone: string;
    email?: string;
    company?: string;
    sector?: string;
  };
}) {
  const fn = httpsCallable(functions, "startWhatsappConversation");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappConversation>;
}

export async function createContactForConversation(payload: {
  conversationId: string;
  name: string;
  email?: string;
  company?: string;
  sector?: string;
}) {
  const fn = httpsCallable(functions, "createWhatsappContact");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappContact>;
}

export async function sendMessage(payload: {
  conversationId: string;
  text: string;
  replyToMessageId?: string;
}) {
  const fn = httpsCallable(functions, "sendWhatsappMessage");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappMessage>;
}

export async function sendReaction(payload: {
  conversationId: string;
  messageId: string;
  emoji: string;
}) {
  const fn = httpsCallable(functions, "sendWhatsappReaction");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function removeReaction(payload: {
  conversationId: string;
  messageId: string;
}) {
  const fn = httpsCallable(functions, "removeWhatsappReaction");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// "Apagar para todos" — só mensagem nossa (direction "out").
export async function deleteMessage(payload: {
  conversationId: string;
  messageId: string;
}) {
  const fn = httpsCallable(functions, "deleteWhatsappMessage");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// Upload direto pro Storage (storage.rules já escopa por companyId, mesmo
// padrão do resto do app) — devolve os dados pra chamar sendMedia em
// seguida. Path: {companyId}/whatsappMedia/{conversationId}/{arquivo}.
export async function uploadWhatsappMedia(
  companyId: string,
  conversationId: string,
  file: File,
): Promise<{ mediaUrl: string; fileName: string; mimeType: string }> {
  const safeName = file.name.replace(/[^\w.-]/g, "_");
  const storagePath = `${companyId}/whatsappMedia/${conversationId}/${Date.now()}-${safeName}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
  const mediaUrl = await getDownloadURL(fileRef);
  return {
    mediaUrl,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
  };
}

export async function sendMedia(payload: {
  conversationId: string;
  mediaUrl: string;
  fileName: string;
  mimeType: string;
}) {
  const fn = httpsCallable(functions, "sendWhatsappMedia");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappMessage>;
}

export async function assignConversation(payload: { conversationId: string }) {
  const fn = httpsCallable(functions, "assignWhatsappConversation");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function transferConversation(payload: {
  conversationId: string;
  targetQueueId?: string;
  targetAgentId?: string;
}) {
  const fn = httpsCallable(functions, "transferWhatsappConversation");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function closeConversation(payload: { conversationId: string }) {
  const fn = httpsCallable(functions, "closeWhatsappConversation");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// "Marcar como lida" manual — pra conversa que não precisa de reply (ex:
// cliente respondeu "ok, vou analisar") mas o atendente já viu e não quer
// mais ver como não lida. Só zera unreadCount, não fecha nem desatribui —
// a conversa continua na listagem normalmente.
export async function markConversationRead(payload: { conversationId: string }) {
  const fn = httpsCallable(functions, "markWhatsappConversationRead");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// ---- Grupos ----
// Sem fila/status/assumir (grupo não tem esse conceito) — por isso não
// reaproveita watchConversations/createConversationThread. Grupo não tem
// conceito de encerrar/reabrir, então não existe a complexidade de costurar
// várias conversas que createConversationThread resolve pro 1:1 — aqui é
// uma coleção só, um único listener ao vivo sem paginação.

export function watchGroups(companyId: string, onData: (list: WhatsappGroup[]) => void) {
  const q = query(
    collection(firestore, `companies/${companyId}/whatsappGroups`),
    where("leftAt", "==", null),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() } as WhatsappGroup)));
  });
}

export function watchGroupMessages(
  companyId: string,
  groupId: string,
  onData: (list: WhatsappMessage[]) => void,
) {
  const q = query(
    collection(firestore, `companies/${companyId}/whatsappGroups/${groupId}/messages`),
    orderBy("timestamp", "asc"),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() } as WhatsappMessage)));
  });
}

// Espelha uploadWhatsappMedia — path {companyId}/whatsappMedia/groups/{groupId}/{arquivo}.
export async function uploadWhatsappGroupMedia(
  companyId: string,
  groupId: string,
  file: File,
): Promise<{ mediaUrl: string; fileName: string; mimeType: string }> {
  const safeName = file.name.replace(/[^\w.-]/g, "_");
  const storagePath = `${companyId}/whatsappMedia/groups/${groupId}/${Date.now()}-${safeName}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
  const mediaUrl = await getDownloadURL(fileRef);
  return {
    mediaUrl,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
  };
}

export async function createGroup(payload: { name: string; contactIds: string[] }) {
  const fn = httpsCallable(functions, "createWhatsappGroup");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappGroup>;
}

export async function addGroupParticipant(payload: { groupId: string; contactIds: string[] }) {
  const fn = httpsCallable(functions, "addWhatsappGroupParticipant");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function removeGroupParticipant(payload: { groupId: string; phone: string }) {
  const fn = httpsCallable(functions, "removeWhatsappGroupParticipant");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function leaveGroup(payload: { groupId: string }) {
  const fn = httpsCallable(functions, "leaveWhatsappGroup");
  const result = await fn(payload);
  return result.data as CallResult<{ instanceLeft: boolean }>;
}

export async function resetGroupUnread(payload: { groupId: string }) {
  const fn = httpsCallable(functions, "resetWhatsappGroupUnread");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// Acesso dentro do Voki (quem pode ver/operar o grupo pela própria conta),
// não participante real do WhatsApp — ver comentário em WhatsappGroup.
export async function addGroupAccess(payload: { groupId: string; userId: string }) {
  const fn = httpsCallable(functions, "addWhatsappGroupAccess");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function removeGroupAccess(payload: { groupId: string; userId: string }) {
  const fn = httpsCallable(functions, "removeWhatsappGroupAccess");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function sendGroupMessage(payload: {
  groupId: string;
  text: string;
  replyToMessageId?: string;
  mentions?: string[];
}) {
  const fn = httpsCallable(functions, "sendWhatsappGroupMessage");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappMessage>;
}

export async function sendGroupMedia(payload: {
  groupId: string;
  mediaUrl: string;
  fileName: string;
  mimeType: string;
}) {
  const fn = httpsCallable(functions, "sendWhatsappGroupMedia");
  const result = await fn(payload);
  return result.data as CallResult<WhatsappMessage>;
}

export async function sendGroupReaction(payload: {
  groupId: string;
  messageId: string;
  emoji: string;
}) {
  const fn = httpsCallable(functions, "sendWhatsappGroupReaction");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

export async function removeGroupReaction(payload: { groupId: string; messageId: string }) {
  const fn = httpsCallable(functions, "removeWhatsappGroupReaction");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}

// "Apagar para todos" — só mensagem nossa (direction "out").
export async function deleteGroupMessage(payload: { groupId: string; messageId: string }) {
  const fn = httpsCallable(functions, "deleteWhatsappGroupMessage");
  const result = await fn(payload);
  return result.data as CallResult<{}>;
}
